/**
   Copyright (c)2021 3Key Company s.r.o., All Rights Reserved

   It is strictly prohibited to distribute the source code,
   transpiled and compiled code or its parts without previous
   agreement of the 3Key Company board.
*/

import "./DataGrid.css";

import React from "react";

import { isElementInParents } from "lib/isElementInParents";

import DataGridBody from "./DataGridBody";

import { DataGridProps, DataGridState } from "./DataGrid.types";

export default class DataGrid extends React.Component<DataGridProps, DataGridState> {

   private componentClassName: string;

   private resizingMouseStartX: number;
   private resizingColumn: number;
   private resizingColumnStartWidth: number;

   constructor(props: DataGridProps) {

      super(props);

      this.componentClassName = "tk-data-grid" + (this.props.componentName ? `-${this.props.componentName}` : "");

      this.resizingMouseStartX = -1;
      this.resizingColumn = 0;
      this.resizingColumnStartWidth = 0;

      const state: DataGridState = {
         columnsState: [],
         dataState: this.props.data,
         focusedItemKey: null,
         selectedItemsKeys: []
      };

      if (this.props.header) {
         this.props.header.columns.forEach(
            (column) => state.columnsState.push({
               filterDropDownVisible: false,
               filterEnabled: false,
               sort: column.sort ? column.sort : "none"
            })
         );
      }

      this.state = state;

   }

   public componentDidUpdate(prevProps: DataGridProps) {

      if (this.props !== prevProps && this.state.dataState !== this.props.data) {

         const state = {
            dataState: this.props.data,
            selectedItemsKeys: this.props.selectedItems ? this.props.selectedItems : this.props.data && this.props.data.length > 0 ? [ this.props.data[0].key ] : []
         }

         if (state.selectedItemsKeys.length > 0 && this.props.onItemSelectionChanged) this.props.onItemSelectionChanged(state.selectedItemsKeys);

         this.setState(state);

      }

   }

   public componentDidMount() {
   }

   public componentWillUnmount() {
      window.removeEventListener("mousedown", this.onWindowMouseDown_Filter);
      window.removeEventListener("mousemove", this.onResizingMouseMove);
      window.removeEventListener("mouseup", this.onWindowMouseUp_Resizer);
   }

   public render(): React.ReactNode {

      const header = this.renderHeader();
      const footer = this.renderFooter();

      return (

         <>
            <div className={this.componentClassName}>

               <div className="container">

                  <div className="spinner" data-visible={this.props.isLoading}></div>

                  <table>
                     {header}
                     <DataGridBody data={this.state.dataState} selectedItemsKeys={this.state.selectedItemsKeys} onRowMouseDown={this.onItemMouseDown} onRowDblClick={this.onItemMouseDblClick}></DataGridBody>
                     {footer}
                  </table>

               </div>

               {this.props.children}

            </div>

         </>
      )

   }

   private renderIcons(column: number): JSX.Element {

      let filter: JSX.Element;
      let sort: JSX.Element;

      let spacer = false;

      if (this.props.header && this.props.header.columns[column].filterable) {
         const filterState = this.state.columnsState[column].filterEnabled ? "enabled" : "disabled";
         filter = (<span className="filter-icon" data-col={column} data-filter={filterState} onMouseDown={this.onFilterMouseDown}></span>);
         spacer = true;
      } else {
         filter = <></>;
      }

      if (this.props.header && this.props.header.columns[column].sortable) {
         sort = (<span className="sort-icon" data-col={column} data-sort={this.state.columnsState[column].sort} onMouseDown={this.onSortMouseDown}></span>);
         spacer = true;
      } else {
         sort = <></>;
      }

      if (spacer) {
         return (<>&nbsp;&nbsp;&nbsp;{filter}{sort}</>);
      } else {
         return (<></>);
      }

   }

   private renderFilterDropDown(column: number): JSX.Element {

      if (!this.props.header) return <></>;
      if (!this.props.header.columns[column].filterable) return <></>;
      if (!this.state.columnsState[column].filterDropDownVisible) return <></>;

      return (
         <div style={{position: "absolute", overflow: "visible"}}>
            <ul>
               <li>Enter filter text:</li>
               <li><input type="text" style={{ width: "100%", boxSizing: "border-box"}}/></li>
            </ul>
         </div>
      );

   }

   private renderHeader(): JSX.Element {

      if (!this.props.header) return <></>;
      if (!this.props.header.visible) return <></>;
      if (this.props.header.columns.length < 1) return <></>;

      const columns: JSX.Element[] = [];
      const filters: JSX.Element[] = [];

      this.props.header.columns.forEach(

         (columnProps, i) => {

            // helper constants
            const prevColumnProps = i > 0 ? this.props.header?.columns[i - 1] : undefined;

            // render left resizing handle
            const resizableBegin = (i > 0 && prevColumnProps?.resizable ?
               <span className="resize-handle-left" onMouseDown={this.onResizerMouseDown} onDoubleClick={this.onResizerDblClick} data-col={i}></span>
               :
               undefined
            );

            // render right resizing handle
            const resizableEnd = ( columnProps.resizable ?
               <span className="resize-handle-right" onMouseDown={this.onResizerMouseDown} onDoubleClick={this.onResizerDblClick} data-col={i + 1}></span>
               :
               undefined
            );

            const filter = this.renderFilterDropDown(i);
            const icons = this.renderIcons(i);

            const columnData = <div>{columnProps.caption}{icons}</div>;

            columns.push(<th key={i}>{resizableBegin}{columnData}{resizableEnd}</th>);
            filters.push(<td key={i}>{filter}</td>)

         }
      )

      return (
         <thead><tr>{columns}</tr><tr>{filters}</tr></thead>
      )

   }

   private renderBody(): JSX.Element {
      return (
         <></>
      )
   }

   private renderFooter(): JSX.Element {

      if (!this.props.footer) return <></>;
      if (!this.props.footer.visible) return <></>;
      if (this.props.footer.columns.length < 1) return <></>;

      const columns: JSX.Element[] = [];

      this.props.footer.columns.forEach(
         (column, i) => {
            columns.push(<th key={i}><div>{column.caption}</div></th>);
         }
      );

      return (
         <tfoot><tr>{columns}</tr></tfoot>
      )
   }


   private onItemMouseDown = (e: React.MouseEvent, key: any) => {

      // const item = this.state.dataState.find(row => row.key === key);

      if (!this.props.multiselect || !e.ctrlKey) {
         const selectedItemsKeys = [ key ];
         this.setState({
            focusedItemKey: key,
            selectedItemsKeys
         });
         if (this.props.onItemSelectionChanged) this.props.onItemSelectionChanged(selectedItemsKeys);
         return;
      }

      if (this.props.multiselect && e.ctrlKey && this.state.selectedItemsKeys.indexOf(key) === -1) {
         const selectedItemsKeys = [ ...this.state.selectedItemsKeys, key ];
         this.setState({
            focusedItemKey: key,
            selectedItemsKeys
         });
         if (this.props.onItemSelectionChanged) this.props.onItemSelectionChanged(selectedItemsKeys);
         return;
      }

      if (this.props.multiselect && e.ctrlKey && this.state.selectedItemsKeys.indexOf(key) >= 0) {
         const selectedItemsKeys = this.state.selectedItemsKeys.filter(fkey => fkey !== key);
         this.setState({
            focusedItemKey: key,
            selectedItemsKeys
         });
         if (this.props.onItemSelectionChanged) this.props.onItemSelectionChanged(selectedItemsKeys);
         return;
      }

   }

   private onItemMouseDblClick = (e: React.MouseEvent, key: any) => {
      if (this.props.onItemDblClick) this.props.onItemDblClick(key);
   }

   private onSortMouseDown = (ev: React.MouseEvent) => {
   }

   private onFilterMouseDown = (ev: React.MouseEvent) => {

      const col: number = parseInt(ev.currentTarget.getAttribute("data-col") as string);
      const colsState = this.state.columnsState.slice(0);

      colsState.forEach(
         (colState, column) => {
            colsState[column].filterDropDownVisible = column === col ? !this.state.columnsState[column].filterDropDownVisible : false;
         }
      );

      this.setState({ columnsState: colsState });

      if (colsState[col].filterDropDownVisible) window.addEventListener("mousedown", this.onWindowMouseDown_Filter);

      ev.preventDefault();
      ev.stopPropagation();

   }

   private onWindowMouseDown_Filter = (ev: MouseEvent) => {

      const filters = document.querySelectorAll(`.${this.componentClassName} .container ul`);
      if (filters.length > 0 && isElementInParents(filters.item(0) as HTMLElement, ev.target as HTMLElement)) return;

      const colsState = this.state.columnsState.slice(0);

      colsState.forEach(
         (colState, column) => {
            colsState[column].filterDropDownVisible = false;
         }
      );

      this.setState({ columnsState: colsState });

      window.removeEventListener("mousedown", this.onWindowMouseDown_Filter);

   }

   private onResizerDblClick = (ev: React.MouseEvent) => {

      const resizingColumn = parseInt(ev.currentTarget.getAttribute("data-col") as string);

      const headerElements = document.querySelectorAll(`.${this.componentClassName} .container thead tr th:nth-child(${resizingColumn}) div`);
      const bodyElements = document.querySelectorAll(`.${this.componentClassName} .container tbody tr td:nth-child(${resizingColumn}) div`);
      const footerElements = document.querySelectorAll(`.${this.componentClassName} .container tfoot tr th:nth-child(${resizingColumn}) div`);

      headerElements.forEach(el => (el as HTMLElement).style.width = "");
      bodyElements.forEach(el => (el as HTMLElement).style.width = "");
      footerElements.forEach(el => (el as HTMLElement).style.width = "");


   }


   private onResizerMouseDown = (ev: React.MouseEvent) => {

      this.resizingMouseStartX = ev.screenX;
      this.resizingColumn = parseInt(ev.currentTarget.getAttribute("data-col") as string);
      this.resizingColumnStartWidth = document.querySelectorAll(`.${this.componentClassName} .container thead tr th:nth-child(${this.resizingColumn}) div`)[0].getBoundingClientRect().width;


      window.addEventListener("mousemove", this.onResizingMouseMove);
      window.addEventListener("mouseup", this.onWindowMouseUp_Resizer);

      ev.preventDefault();
      ev.stopPropagation();
   }


   private onResizingMouseMove = (ev: MouseEvent) => {

      const distance = ev.screenX - this.resizingMouseStartX;
      const multiplier = window.devicePixelRatio > 1.5 ? 1.25 : 1;
      const newWidth = this.resizingColumnStartWidth as number + (distance * multiplier);

      const headerElements = document.querySelectorAll(`.${this.componentClassName} .container  thead tr th:nth-child(${this.resizingColumn}) div`);
      const bodyElements = document.querySelectorAll(`.${this.componentClassName} .container  tbody tr td:nth-child(${this.resizingColumn}) div`);
      const footerElements = document.querySelectorAll(`.${this.componentClassName} .container  tfoot tr th:nth-child(${this.resizingColumn}) div`);

      headerElements.forEach(el => (el as HTMLElement).style.width = newWidth + "px");
      bodyElements.forEach(el => (el as HTMLElement).style.width = newWidth + "px");
      footerElements.forEach(el => (el as HTMLElement).style.width = newWidth + "px");


   }


   private onWindowMouseUp_Resizer = (ev: MouseEvent) => {

      window.removeEventListener("mousemove", this.onResizingMouseMove);
      window.removeEventListener("mouseup", this.onWindowMouseUp_Resizer);
   }

}
